// Per-customer SMS composer. Pre-fills the standard invoice-due
// message; Mark can edit before tapping Send. Tokens [Name], [amount],
// and [link] are substituted server-side at send time (customers
// always see real values, not literals).
//
// On send: calls /sendSmsGateway which writes to /smsLog and returns id + ok.
// On success: flash + auto-close in 1.5s. On failure: flash + stay open
// so Mark can retry or copy the message.

import { useState } from 'react'
import { sendSmsToCustomer } from '../lib/stripe.js'
import { fmtMoney } from '../lib/format.js'

const DEFAULT_TEMPLATE =
  'Hi [Name], this is YesNet WiFi. Your invoice of $[amount] is due. ' +
  'Pay here: [link].'

export default function SmsComposerModal({ customer, customerId, onClose }) {
  const [message, setMessage] = useState(DEFAULT_TEMPLATE)
  const [busy, setBusy]       = useState(false)
  const [flash, setFlash]     = useState(null)

  const firstName  = (customer?.name || '').trim().split(/\s+/)[0] || 'there'
  const amountText = customer?.monthlyAmount != null ? fmtMoney(customer.monthlyAmount) : '$—'
  const previewLink = `https://wifibilling.web.app/pay?c=${customerId}`
  const preview = message
    .replace(/\[Name\]/g, firstName)
    .replace(/\[name\]/g, firstName)
    .replace(/\[amount\]/g, amountText.replace('$', ''))
    .replace(/\[link\]/g, previewLink)
    .replace(/\[unique payment link\]/g, previewLink)
  const charCount = preview.length
  const segments  = Math.max(1, Math.ceil(charCount / 160))

  const onSend = async () => {
    if (busy) return
    setBusy(true)
    setFlash(null)
    try {
      const r = await sendSmsToCustomer(customerId, message)
      setFlash({ kind: 'ok', msg: `Sent · ${r.id || r.sid || 'OK'}` })
      setTimeout(onClose, 1400)
    } catch (e) {
      setFlash({ kind: 'err', msg: e.message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Backdrop onClose={onClose}>
      <div className="card max-w-md w-full">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Send SMS</div>
            <h2 className="text-lg font-extrabold mt-0.5">{customer?.name || 'Customer'}</h2>
            <div className="text-xs text-gray-500">{formatPhone(customer?.phone) || 'no phone on file'}</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="btn-ghost text-lg">✕</button>
        </div>

        <div className="mt-4">
          <label className="label flex items-center justify-between">
            <span>Message</span>
            <span className="text-[10px] text-gray-400 normal-case tracking-normal">
              {charCount} chars · {segments} segment{segments === 1 ? '' : 's'}
            </span>
          </label>
          <textarea
            className="input min-h-[120px] resize-y"
            value={message}
            onChange={(e) => setMessage(e.target.value)} />
          <p className="text-[11px] text-gray-500 mt-1">
            Tokens: <code>[Name]</code> · <code>[amount]</code> · <code>[link]</code> — filled at send time.
          </p>
        </div>

        <div className="mt-3 rounded-md bg-gray-50 border border-gray-200 p-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1">Preview</div>
          <p className="text-sm whitespace-pre-wrap text-gray-800">{preview}</p>
        </div>

        {flash && (
          <div className={`mt-3 rounded-md p-3 text-sm font-bold ${
            flash.kind === 'ok'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>{flash.msg}</div>
        )}

        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button
            onClick={onSend}
            disabled={busy || !customer?.phone}
            className="btn-primary flex-1 disabled:opacity-50">
            {busy ? 'Sending…' : 'Send SMS'}
          </button>
        </div>
      </div>
    </Backdrop>
  )
}

function Backdrop({ onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}>
      <div className="mt-12" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  )
}

function formatPhone(p) {
  const d = String(p || '').replace(/\D+/g, '')
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`
  if (d.length === 11 && d.startsWith('1')) return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`
  return p || ''
}
